import { DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { ActionIcon, Badge, Button, Group } from '@repletoryreach/sdk-ui';
import { Sparkline } from '@repletoryreach/sdk-ui/charts';
import { Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { TwoLineCell } from '../../../components/TwoLineCell';
import type { Indexed } from '../../../lib/types';
import { formatInteger, formatRate } from '../../../lib/format';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { BACKLOG_WARN, hasNoConsumers, isBacklogged, isDeadLetterQueue, queueKey, queueRate, queueTone, type Queue, type QueueSamples } from './queueState';

export function QueuesTable({ rows, loading, history, onOpen, onRequeue }: {
  rows: Queue[];
  loading: boolean;
  /** One sample per poll, keyed by vhost/name: the trend beside each figure and the rate. */
  history: Record<string, QueueSamples>;
  onOpen: (q: Queue) => void;
  onRequeue: (q: Queue) => void;
}) {
  const t = useT();
  return (
    <DataTable<Indexed<Queue>>
      records={rows as Indexed<Queue>[]}
      loading={loading}
      idAccessor={(r) => queueKey(r)}
      emptyState={t('queues.emptyRows')}
      highlightOnHover
      onRowClick={(r) => onOpen(r)}
      columns={[
        {
          accessor: 'name',
          title: t('queues.columns.queue'),
          sortable: true,
          // RabbitMQ scopes a queue name to a vhost, so the vhost is the
          // qualifier the name needs rather than a column of its own.
          render: (r) => (
            <TwoLineCell primary={<code>{r.name}</code>} secondary={r.vhost} title={r.name} />
          )
        },
        {
          accessor: 'state',
          title: t('queues.columns.state'),
          render: (r) => (
            <Group gap="xs" wrap="nowrap">
              <StatusBadge status={queueTone(r)} size="xs">{r.state ?? t('status.unknown')}</StatusBadge>
              {hasNoConsumers(r) && (
                <Badge size="xs" color="red" variant="light">{t('queues.noConsumersBadge')}</Badge>
              )}
              {/* Says why this row, and not its neighbour, carries a requeue
                  button in the actions column. */}
              {isDeadLetterQueue(r) && (
                <Tooltip label={t('queues.deadLetterTooltip')} position="top">
                  <Badge size="xs" color="orange" variant="light">{t('queues.deadLetterBadge')}</Badge>
                </Tooltip>
              )}
            </Group>
          )
        },
        {
          accessor: 'messages_ready',
          title: t('queues.columns.ready'),
          textAlign: 'right',
          sortable: true,
          render: (r) => {
            const badge = (
              <Badge size="xs" variant="light" color={isBacklogged(r) ? 'orange' : 'gray'}>
                {formatInteger(r.messages_ready ?? 0)}
              </Badge>
            );
            // The threshold is stated where it applies, so the orange badge is
            // not an unexplained colour.
            const figure = isBacklogged(r)
              ? <Tooltip label={t('queues.backlogTooltip', { threshold: BACKLOG_WARN })} position="top">{badge}</Tooltip>
              : badge;
            // A depth of 1,248 says nothing on its own: rising or draining is
            // the question. The series is what this page has watched since it
            // was opened, so it starts empty and fills as it polls.
            const series = history[queueKey(r)]?.ready ?? [];
            return (
              <Group gap="xs" wrap="nowrap" justify="flex-end">
                {figure}
                {series.length > 1 && (
                  <span title={t('queues.trendTooltip')}>
                    <Sparkline data={series} w={54} h={20} color="var(--rr-color-warning)" />
                  </span>
                )}
              </Group>
            );
          }
        },
        {
          accessor: 'messages_unacked',
          title: t('queues.columns.unacked'),
          textAlign: 'right',
          sortable: true,
          render: (r) => {
            const series = history[queueKey(r)]?.unacked ?? [];
            return (
              <Group gap="xs" wrap="nowrap" justify="flex-end">
                <Badge size="xs" variant="light" color={(r.messages_unacked ?? 0) > 0 ? 'red' : 'gray'}>
                  {formatInteger(r.messages_unacked ?? 0)}
                </Badge>
                {series.length > 1 && (
                  <span title={t('queues.trendTooltip')}>
                    <Sparkline data={series} w={54} h={20} color="var(--rr-color-error)" />
                  </span>
                )}
              </Group>
            );
          }
        },
        {
          accessor: 'publish_rate',
          title: t('queues.columns.rate'),
          textAlign: 'right',
          sortable: true,
          // In and out together: a queue publishing faster than it delivers is
          // the shape of a backlog forming, which a depth alone cannot show.
          render: (r) => {
            const rate = queueRate(r, history[queueKey(r)]);
            return (
              <Tooltip label={rate.measured ? t('queues.rateTooltip') : t('queues.rateTooltipBroker')} position="top">
                <span>{`${formatRate(rate.publish)} / ${formatRate(rate.deliver)}`}</span>
              </Tooltip>
            );
          }
        },
        { accessor: 'consumers', title: t('queues.columns.consumers'), textAlign: 'right', sortable: true },
        { accessor: 'messages', title: t('common.total'), textAlign: 'right', sortable: true, render: (r) => formatInteger(r.messages ?? 0) },
        {
          accessor: 'actions',
          title: t('common.actions'),
          textAlign: 'right',
          render: (r) => <QueueRowActions queue={r} t={t} onRequeue={onRequeue} />
        }
      ]}
    />
  );
}

/**
 * Requeue is a recovery action, so it is one click away on the queues it
 * recovers: a dead-letter queue holding messages gets a button, everything else
 * gets the menu. Offering the same button on every row would make the rare,
 * consequential action look routine.
 */
function QueueRowActions({ queue, t, onRequeue }: { queue: Queue; t: Translate; onRequeue: (q: Queue) => void }) {
  const prominent = isDeadLetterQueue(queue) && (queue.messages ?? 0) > 0;
  return (
    // The row itself opens the detail drawer, so these controls keep their clicks.
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
      {prominent && (
        <Button size="xs" variant="light" color="orange" onClick={() => onRequeue(queue)}>
          {t('queues.requeue.actionShort')}
        </Button>
      )}
      <Menu position="bottom-end" width={220}>
        <Menu.Target>
          <ActionIcon variant="subtle" size="sm" aria-label={t('queues.rowMenuLabel', { name: queue.name })}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
              <circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" />
            </svg>
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => onRequeue(queue)}>{t('queues.requeue.action')}</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </span>
  );
}
